/**
 * System presets for the systems-laboratory experiment.
 * These are the canned system-of-equations problems students can work through.
 */

export interface SystemPreset {
  id: string;
  equations: readonly { lhs: string; rhs: string }[];
  vars: readonly string[];
}

export const SYSTEM_PRESETS: readonly SystemPreset[] = [
  // Foundation: 2×2 systems
  {
    id: 'foundation-2x2-1',
    equations: [
      { lhs: '2x + y', rhs: '7' },
      { lhs: 'x - y', rhs: '2' },
    ],
    vars: ['x', 'y'],
  },
  {
    id: 'foundation-2x2-2',
    equations: [
      { lhs: '3x + 2y', rhs: '11' },
      { lhs: 'x + y', rhs: '4' },
    ],
    vars: ['x', 'y'],
  },
  {
    id: 'foundation-2x2-3',
    equations: [
      { lhs: 'x + 2y', rhs: '5' },
      { lhs: '2x - y', rhs: '5' },
    ],
    vars: ['x', 'y'],
  },

  // Standard: 3×3 systems
  {
    id: 'standard-3x3-1',
    equations: [
      { lhs: 'x + y + z', rhs: '6' },
      { lhs: '2x - y + z', rhs: '3' },
      { lhs: 'x + 2y - z', rhs: '2' },
    ],
    vars: ['x', 'y', 'z'],
  },
  {
    id: 'standard-3x3-2',
    equations: [
      { lhs: '2x + y - z', rhs: '8' },
      { lhs: 'x - y + 2z', rhs: '1' },
      { lhs: '3x + y + z', rhs: '10' },
    ],
    vars: ['x', 'y', 'z'],
  },

  // Special: parallel lines — inconsistent, no solution
  {
    id: 'special-parallel',
    equations: [
      { lhs: 'x - y', rhs: '2' },
      { lhs: 'x - y', rhs: '5' },
    ],
    vars: ['x', 'y'],
  },
  // Special: coincident lines — dependent, infinitely many
  {
    id: 'special-coincident',
    equations: [
      { lhs: 'x + 2y', rhs: '5' },
      { lhs: '2x + 4y', rhs: '10' },
    ],
    vars: ['x', 'y'],
  },
];

/**
 * Validate that a preset has all required fields and valid structure.
 */
export function isValidPreset(p: SystemPreset): boolean {
  // id must be non-empty string
  if (typeof p.id !== 'string' || p.id.length === 0) {
    return false;
  }

  // equations must be non-empty array
  if (!Array.isArray(p.equations) || p.equations.length === 0 || p.equations.length > 3) {
    return false;
  }

  // each equation must have lhs and rhs as strings
  for (const eq of p.equations as unknown as { lhs: unknown; rhs: unknown }[]) {
    if (typeof eq.lhs !== 'string' || typeof eq.rhs !== 'string') {
      return false;
    }
    if (eq.lhs.length === 0 || eq.rhs.length === 0) {
      return false;
    }
  }

  // vars must be non-empty array of strings
  if (!Array.isArray(p.vars) || p.vars.length === 0 || p.vars.length > 3) {
    return false;
  }
  for (const v of p.vars) {
    if (typeof v !== 'string' || v.length === 0) {
      return false;
    }
  }

  return true;
}
